// frontend/src/components/CrearOrdenCompraMultipleModal.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import presupuestosService from '../services/presupuestosService';
import './CrearModal.css';

const CrearOrdenCompraMultipleModal = ({
  recursosSeleccionados,
  presupuestoId,
  partida,
  area,
  onClose,
  onCreated
}) => {
  const [proveedores, setProveedores] = useState([]);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disponibilidades, setDisponibilidades] = useState({});
  const [itemsData, setItemsData] = useState([]);

  useEffect(() => {
    cargarProveedores();
    cargarDisponibilidades();
  }, []);

  const cargarProveedores = async () => {
    try {
      const response = await presupuestosService.getProveedores();
      let proveedoresData = [];

      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        proveedoresData = response.data.results;
      } else if (Array.isArray(response.data)) {
        proveedoresData = response.data;
      }

      setProveedores(proveedoresData);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const cargarDisponibilidades = async () => {
    try {
      const disponibilidadesTemp = {};
      const itemsTemp = [];

      for (const recurso of recursosSeleccionados) {
        try {
          const response = await presupuestosService.getDisponibilidadRecurso(recurso.id);
          disponibilidadesTemp[recurso.id] = response.data;

          // Inicializar datos del item con cantidad disponible
          itemsTemp.push({
            recurso,
            cantidad: response.data.cantidad_disponible,
            valor_unitario: recurso.valor_unitario
          });
        } catch (error) {
          console.error(`Error al cargar disponibilidad de recurso ${recurso.id}:`, error);
          disponibilidadesTemp[recurso.id] = {
            cantidad_disponible: 0,
            cantidad_total: recurso.cantidad,
            cantidad_en_ocs: 0
          };
          itemsTemp.push({
            recurso,
            cantidad: 0,
            valor_unitario: recurso.valor_unitario
          });
        }
      }

      setDisponibilidades(disponibilidadesTemp);
      setItemsData(itemsTemp);
    } catch (error) {
      console.error('Error al cargar disponibilidades:', error);
      toast.error('Error al cargar disponibilidades');
    }
  };

  const handleProveedorChange = (e) => {
    const proveedorId = e.target.value;
    if (proveedorId) {
      const proveedor = proveedores.find(p => p.id === parseInt(proveedorId));
      setSelectedProveedor(proveedor || null);
    } else {
      setSelectedProveedor(null);
    }
  };

  const handleItemChange = (recursoId, field, value) => {
    setItemsData(prev => prev.map(item => {
      if (item.recurso.id === recursoId) {
        return {
          ...item,
          [field]: value
        };
      }
      return item;
    }));
  };

  const calcularMontoTotal = () => {
    return itemsData.reduce((total, item) => {
      const cantidad = parseFloat(item.cantidad) || 0;
      const valorUnitario = parseFloat(item.valor_unitario) || 0;
      return total + (cantidad * valorUnitario);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (itemsData.length === 0) {
      toast.error('No hay items seleccionados');
      return;
    }

    // Validar que todos los items tengan cantidad y valor
    for (const item of itemsData) {
      const cantidad = parseFloat(item.cantidad);
      const valorUnitario = parseFloat(item.valor_unitario);

      if (!cantidad || cantidad <= 0) {
        toast.error(`La cantidad para "${item.recurso.nombre_recurso}" debe ser mayor a 0`);
        return;
      }

      if (!valorUnitario || valorUnitario <= 0) {
        toast.error(`El valor unitario para "${item.recurso.nombre_recurso}" debe ser mayor a 0`);
        return;
      }

      // Validar disponibilidad
      const disponibilidad = disponibilidades[item.recurso.id];
      if (disponibilidad && cantidad > disponibilidad.cantidad_disponible) {
        toast.error(`La cantidad para "${item.recurso.nombre_recurso}" no puede exceder ${disponibilidad.cantidad_disponible}`);
        return;
      }
    }

    setLoading(true);
    try {
      // Crear una orden de compra por cada item
      const promises = itemsData.map(item => {
        return presupuestosService.createOrdenCompra({
          presupuesto: presupuestoId,
          recurso: item.recurso.id,
          proveedor: selectedProveedor ? selectedProveedor.id : null,
          cantidad: parseFloat(item.cantidad),
          valor_unitario: parseFloat(item.valor_unitario)
        });
      });

      await Promise.all(promises);
      toast.success(`${itemsData.length} orden(es) de compra creada(s) exitosamente`);
      onCreated();
    } catch (error) {
      console.error('Error al crear órdenes de compra:', error);
      const errorMsg = error.response?.data?.cantidad?.[0] || 'Error al crear órdenes de compra';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Crear Órdenes de Compra ({recursosSeleccionados.length} items)</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Información de contexto */}
          <div className="info-box">
            <p><strong>Presupuesto:</strong> {area?.nombre_area || 'N/A'} - {partida?.nombre_partida || 'N/A'}</p>
          </div>

          {/* Selección de proveedor */}
          <div className="form-group">
            <label>Proveedor (Opcional)</label>
            <select
              value={selectedProveedor?.id || ''}
              onChange={handleProveedorChange}
            >
              <option value="">Sin proveedor</option>
              {Array.isArray(proveedores) && proveedores.map(proveedor => (
                <option key={proveedor.id} value={proveedor.id}>
                  {proveedor.nombre_proveedor}
                </option>
              ))}
            </select>
          </div>

          {/* Información del proveedor seleccionado */}
          {selectedProveedor && (
            <div className="proveedor-info-box">
              <h4>📋 Información del Proveedor</h4>
              <div className="info-grid">
                {selectedProveedor.rut && (
                  <div className="info-item">
                    <span className="info-label">RUT:</span>
                    <span className="info-value">{selectedProveedor.rut}</span>
                  </div>
                )}
                {selectedProveedor.email && (
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{selectedProveedor.email}</span>
                  </div>
                )}
                {selectedProveedor.mail && (
                  <div className="info-item">
                    <span className="info-label">Mail Alternativo:</span>
                    <span className="info-value">{selectedProveedor.mail}</span>
                  </div>
                )}
                {selectedProveedor.telefono && (
                  <div className="info-item">
                    <span className="info-label">Teléfono:</span>
                    <span className="info-value">{selectedProveedor.telefono}</span>
                  </div>
                )}
                {selectedProveedor.contacto && (
                  <div className="info-item">
                    <span className="info-label">Contacto:</span>
                    <span className="info-value">{selectedProveedor.contacto}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lista de items seleccionados */}
          <div className="items-seleccionados-section">
            <h3>Items Seleccionados</h3>
            <div className="items-list">
              {itemsData.map((item, index) => {
                const disponibilidad = disponibilidades[item.recurso.id];
                const monto = (parseFloat(item.cantidad) || 0) * (parseFloat(item.valor_unitario) || 0);

                return (
                  <div key={item.recurso.id} className="item-card">
                    <div className="item-header">
                      <h4>{index + 1}. {item.recurso.nombre_recurso}</h4>
                      {item.recurso.proveedor_nombre && (
                        <span className="item-proveedor-badge">
                          Proveedor sugerido: {item.recurso.proveedor_nombre}
                        </span>
                      )}
                    </div>

                    {disponibilidad && (
                      <div className="disponibilidad-info">
                        <div className="disp-row">
                          <span>Total en presupuesto:</span>
                          <span className="disp-value">
                            {disponibilidad.cantidad_total.toLocaleString('es-CL')} {disponibilidad.recurso_unidad || 'unidades'}
                          </span>
                        </div>
                        <div className="disp-row">
                          <span>En órdenes previas:</span>
                          <span className="disp-value">
                            {disponibilidad.cantidad_en_ocs.toLocaleString('es-CL')}
                          </span>
                        </div>
                        <div className="disp-row highlight">
                          <span><strong>Disponible:</strong></span>
                          <span className="disp-value disponible">
                            {disponibilidad.cantidad_disponible.toLocaleString('es-CL')}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="item-inputs">
                      <div className="input-group">
                        <label>
                          Cantidad * (máx: {disponibilidad?.cantidad_disponible.toLocaleString('es-CL') || 0})
                        </label>
                        <input
                          type="number"
                          value={item.cantidad}
                          onChange={(e) => handleItemChange(item.recurso.id, 'cantidad', e.target.value)}
                          min="0"
                          step="0.01"
                          max={disponibilidad?.cantidad_disponible || 0}
                          required
                        />
                      </div>

                      <div className="input-group">
                        <label>Valor Unitario *</label>
                        <input
                          type="number"
                          value={item.valor_unitario}
                          onChange={(e) => handleItemChange(item.recurso.id, 'valor_unitario', e.target.value)}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      <div className="input-group">
                        <label>Monto</label>
                        <div className="monto-display-inline">
                          ${monto.toLocaleString('es-CL')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Monto total */}
          <div className="monto-total-section">
            <div className="monto-total-display">
              <span>Monto Total de Órdenes:</span>
              <span className="monto-total-value">
                ${calcularMontoTotal().toLocaleString('es-CL')}
              </span>
            </div>
          </div>

          {/* Acciones */}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || itemsData.length === 0}
            >
              {loading ? 'Creando...' : `Crear ${itemsData.length} Orden(es) de Compra`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrearOrdenCompraMultipleModal;
